import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ServiceItem } from '../../types/service';
import { useUI } from '../../context/UIContext';
import { useAppSelector } from '../../hooks/useRedux';
import { VariationTransformer } from '../../utils/ServiceVariationTransformer';
import serviceTemplateService from '../../services/serviceTemplateService';
import type { SnackbarShowOptions } from '../../context/UIContext';
import '../../styles/EditTemplatePage.css';

/** Errors on this screen use a centered toast so they aren’t missed at the bottom edge. */
const SNACKBAR_CENTER: SnackbarShowOptions = { position: 'center' };

const EditTemplatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar, setLoading } = useUI();
  const auth = useAppSelector((state) => state.auth);
  const [loading, setLocalLoading] = useState(false);
  
  // Make sure we have template data from location state
  const templateData = location.state?.template as ServiceItem | undefined;
  const [template] = useState<ServiceItem | null>(templateData || null);
  
  // If no template in location state, redirect back to template selection
  useEffect(() => {
    if (!templateData) {
      showSnackbar('No template selected. Please choose a template first.', 'error', SNACKBAR_CENTER);
      navigate('/stylist/service-template');
    }
  }, [templateData, navigate, showSnackbar]);

  const [serviceData, setServiceData] = useState(template ? {
    name: template.name,
    description: typeof template.description === 'string'
      ? template.description
      : template.description?.short || '',
    duration: {
      base: template.baseDuration.min,
      min: template.baseDuration.min,
      max: template.baseDuration.max,
    },
    price: {
      base: template.basePrice.min,
      min: template.basePrice.min,
      max: template.basePrice.max,
    },
  } : {
    name: '',
    description: '',
    duration: { base: 30, min: 30, max: 60 },
    price: { base: 50, min: 50, max: 100 },
  });

  const handleCreateService = async () => {
    try {
      // Make sure we have a template
      if (!template) {
        showSnackbar('No template selected', 'error', SNACKBAR_CENTER);
        navigate('/stylist/service-template');
        return;
      }
      
      // Show loading state (matching mobile behavior)
      setLoading(true);
      setLocalLoading(true);
      console.log('Creating service from template:', template);
      
      // Transform service variations from template
      const variations = VariationTransformer.transform(template);
      
      const servicePayload = {
        name: serviceData.name,
        category: template.category,
        subcategory: template.subcategory,
        description: serviceData.description,
        price: {
          base: serviceData.price.base,
          min: serviceData.price.min,
          max: serviceData.price.max
        },
        duration: {
          base: serviceData.duration.base,
          min: serviceData.duration.min,
          max: serviceData.duration.max
        },
        variations: variations || {
          hair: [],
          size: [],
          complexity: []
        },
        requirements: {
          consultation: {
            required: template.requiresConsultation || false,
            duration: 30
          },
          preparation: template.customRequirements || [],
          products: []
        },
        isEnabled: true,
        service: template._id,
        stylist: auth.user?.id || '',
      };

      console.log('Sending service payload:', servicePayload);
      const response = await serviceTemplateService.useTemplate(servicePayload);
      console.log('Service created response:', response);

      // Backend returns { success: true, data: stylistService }
      // The response from useTemplate is the axios response.data, which is { success: true, data: stylistService }
      if (response && response.success && response.data && response.data._id) {
        setLoading(false);
        setLocalLoading(false);
        const serviceName = serviceData.name || template.name || 'Service';
        showSnackbar(`Service "${serviceName}" added`, 'success');
        // Small delay before redirect to show success message
        setTimeout(() => {
          navigate('/stylist/service-management');
        }, 1000);
      } else {
        const body = response && typeof response === 'object' ? (response as Record<string, unknown>) : null;
        const msg = typeof body?.message === 'string' ? body.message : '';
        const lower = msg.toLowerCase();
        const code = body?.code != null ? String(body.code) : '';
        const looksDuplicate =
          lower.includes('already exists') ||
          lower.includes('duplicate') ||
          lower.includes('conflict') ||
          code.toUpperCase().includes('DUPLICATE');
        console.warn('[EditTemplate] Unexpected create-from-template response:', response);
        showSnackbar(
          looksDuplicate
            ? 'This service already exists. You cannot duplicate it.'
            : 'Could not create service. Please try again.',
          'error',
          SNACKBAR_CENTER
        );
        setLoading(false);
        setLocalLoading(false);
      }
    } catch (error) {
      console.error('Error creating service:', error);
      // apiClient response interceptor rejects `{ status, message, data }`, not a raw AxiosError
      const err = error as any;
      const apiErrorMessage =
        err?.data?.message ||
        err?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data?.error;
      const httpStatus = err?.status ?? err?.response?.status;
      const fallbackMsg =
        (typeof err?.message === 'string' && err.message) ||
        (error instanceof Error ? error.message : '');
      const combinedMessage = apiErrorMessage || fallbackMsg;
      const normalizedApiMessage = String(combinedMessage || '').toLowerCase();
      const isDuplicateError =
        httpStatus === 409 ||
        normalizedApiMessage.includes('already exists') ||
        normalizedApiMessage.includes('duplicate');
      const errorMessage = isDuplicateError
        ? 'This service already exists. You cannot duplicate it.'
        : (combinedMessage && combinedMessage !== 'An error occurred'
            ? combinedMessage
            : 'Failed to create service');
      showSnackbar(errorMessage, 'error', SNACKBAR_CENTER);
      setLoading(false);
      setLocalLoading(false);
    }
  };

  const validateInputs = () => {
    if (!serviceData.name.trim()) {
      showSnackbar('Service name is required', 'error', SNACKBAR_CENTER);
      return false;
    }
    if (serviceData.duration.min <= 0 || serviceData.duration.max <= 0) {
      showSnackbar('Duration must be greater than 0', 'error', SNACKBAR_CENTER);
      return false;
    }
    if (serviceData.price.min <= 0 || serviceData.price.max <= 0) {
      showSnackbar('Price must be greater than 0', 'error', SNACKBAR_CENTER);
      return false;
    }
    if (serviceData.duration.min > serviceData.duration.max) {
      showSnackbar('Minimum duration cannot be greater than maximum duration', 'error', SNACKBAR_CENTER);
      return false;
    }
    if (serviceData.price.min > serviceData.price.max) {
      showSnackbar('Minimum price cannot be greater than maximum price', 'error', SNACKBAR_CENTER);
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent double submission
    if (loading) {
      return;
    }
    if (validateInputs()) {
      handleCreateService();
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setServiceData((prev) => {
      const updated = { ...prev };
      
      if (field === 'name') {
        updated.name = value as string;
      } else if (field === 'description') {
        updated.description = value as string;
      } else if (field === 'duration.min' || field === 'duration.max') {
        const numValue = typeof value === 'string' ? parseInt(value) || 0 : value;
        const key = field.split('.')[1] as 'min' | 'max';
        updated.duration[key] = numValue;
        
        // Also update base if min is changed
        if (key === 'min') {
          updated.duration.base = numValue;
        }
      } else if (field === 'price.min' || field === 'price.max') {
        const numValue = typeof value === 'string' ? parseInt(value) || 0 : value;
        const key = field.split('.')[1] as 'min' | 'max';
        updated.price[key] = numValue;
        
        // Also update base if min is changed
        if (key === 'min') {
          updated.price.base = numValue;
        }
      }
      
      return updated;
    });
  };

  return (
    <div className="tesla-layout">
      <div className="tesla-section-header">
        <button 
          className="tesla-button tesla-button--outline tesla-button--icon-only" 
          onClick={() => navigate(-1)}
        >
          <i className="icon-arrow-left"></i>
        </button>
        <h1 className="tesla-title">Edit Service Template</h1>
      </div>
      
      <div className="service-template-editor">
        <form onSubmit={handleSubmit} className="tesla-form">
          <div className="tesla-form-group">
            <label htmlFor="service-name" className="tesla-label">Service Name</label>
            <input
              id="service-name"
              type="text"
              className="tesla-input"
              value={serviceData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter service name"
              required
            />
            {template && (
              <p className="tesla-hint" style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                Based on template: {template.category} → {template.subcategory}
              </p>
            )}
          </div>

          <div className="tesla-form-group">
            <label htmlFor="service-description" className="tesla-label">Description</label>
            <textarea
              id="service-description"
              className="tesla-textarea"
              value={serviceData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter service description"
              rows={4}
            />
          </div>

          <div className="tesla-form-group">
            <label className="tesla-label">Duration (minutes)</label>
            <div className="tesla-input-row">
              <div className="tesla-input-group">
                <label htmlFor="duration-min" className="tesla-sublabel">Minimum</label>
                <input
                  id="duration-min"
                  type="number"
                  className="tesla-input"
                  value={serviceData.duration.min}
                  onChange={(e) => handleInputChange('duration.min', e.target.value)}
                  min="1"
                  required
                />
              </div>
              <div className="tesla-input-group">
                <label htmlFor="duration-max" className="tesla-sublabel">Maximum</label>
                <input
                  id="duration-max"
                  type="number"
                  className="tesla-input"
                  value={serviceData.duration.max}
                  onChange={(e) => handleInputChange('duration.max', e.target.value)}
                  min="1"
                  required
                />
              </div>
            </div>
          </div>

          <div className="tesla-form-group">
            <label className="tesla-label">Price ($)</label>
            <div className="tesla-input-row">
              <div className="tesla-input-group">
                <label htmlFor="price-min" className="tesla-sublabel">Minimum</label>
                <input
                  id="price-min"
                  type="number"
                  className="tesla-input"
                  value={serviceData.price.min}
                  onChange={(e) => handleInputChange('price.min', e.target.value)}
                  min="1"
                  required
                />
              </div>
              <div className="tesla-input-group">
                <label htmlFor="price-max" className="tesla-sublabel">Maximum</label>
                <input
                  id="price-max"
                  type="number"
                  className="tesla-input"
                  value={serviceData.price.max}
                  onChange={(e) => handleInputChange('price.max', e.target.value)}
                  min="1"
                  required
                />
              </div>
            </div>
          </div>

          <div className="tesla-form-actions">
            <button 
              type="button" 
              className="tesla-button tesla-button--outline" 
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="tesla-button tesla-button--primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span style={{ display: 'inline-block', marginRight: '8px' }}>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ animation: 'spin 1s linear infinite' }}
                    >
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25"></circle>
                      <path d="M12 2a10 10 0 0 1 10 10" strokeDasharray="20 20"></path>
                    </svg>
                  </span>
                  Saving...
                </>
              ) : (
                'Add Service'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTemplatePage;